import React, { useState, useEffect } from 'react';
import { uploadImportPreview, commitImport } from '../api/imports';
import { fetchDecks } from '../api/decks';
import { Deck } from '../types/deck';
import {
  ImportFormat,
  DuplicatePolicy,
  ImportPreviewResponse,
  ImportCommitResponse,
} from '../types/import';
import { formatExampleText } from '../types/word';
import { useToast } from '../context/ToastContext';
import {
  UploadCloud,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  RefreshCw,
  Copy,
  Info,
  Check,
} from 'lucide-react';

interface ImportPageProps {
  onNavigateToStudy: () => void;
  onNavigateToWordbook: () => void;
}

export const ImportPage: React.FC<ImportPageProps> = ({
  onNavigateToStudy,
  onNavigateToWordbook,
}) => {
  const { showToast } = useToast();

  const [decks, setDecks] = useState<Deck[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState<string>('');
  const [format, setFormat] = useState<ImportFormat>('auto');
  const [duplicatePolicy, setDuplicatePolicy] = useState<DuplicatePolicy>('skip');

  // File state
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<ImportPreviewResponse | null>(null);

  // Commit state
  const [committing, setCommitting] = useState<boolean>(false);
  const [commitResult, setCommitResult] = useState<ImportCommitResponse | null>(null);

  // Sample copied flag
  const [copiedFormat, setCopiedFormat] = useState<string | null>(null);

  useEffect(() => {
    fetchDecks()
      .then((data) => setDecks(data || []))
      .catch(() => {});
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (selected.size > 10 * 1024 * 1024) {
        showToast('文件大小超过 10MB 限制', 'error');
        return;
      }
      setFile(selected);
      setPreviewData(null);
      setCommitResult(null);
    }
  };

  const handlePreviewUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      showToast('请先选择要导入的文件', 'warning');
      return;
    }

    setUploading(true);
    setCommitResult(null);
    try {
      const data = await uploadImportPreview(file, format, selectedDeckId || undefined);
      setPreviewData(data);
      showToast(`解析完成：有效 ${data.valid_count} 条，重复 ${data.duplicates} 条，错误 ${data.error_count} 条`, 'info');
    } catch (err: any) {
      showToast(err.message || '文件上传或解析失败', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleCommit = async () => {
    if (!previewData) return;
    setCommitting(true);
    try {
      const result = await commitImport(previewData.id, {
        deck_id: selectedDeckId || undefined,
        duplicate_policy: duplicatePolicy,
      });
      setCommitResult(result);
      showToast('生词导入完成！', 'success');
    } catch (err: any) {
      showToast(err.message || '导入入库失败', 'error');
    } finally {
      setCommitting(false);
    }
  };

  const sampleTemplates: Record<string, string> = {
    csv: `term,meaning,phonetic,examples,tags\nresilient,"adj. 有弹性的; 能迅速恢复的",rɪˈzɪliənt,"He is resilient.","GRE;高频"\nephemeral,"adj. 短暂的; 朝生夕死的",ɪˈfemərəl,"Ephemeral pleasure.","写作"`,
    json: `[\n  {\n    "term": "resilient",\n    "meaning": "adj. 有弹性的; 能迅速恢复的",\n    "phonetic": "rɪˈzɪliənt",\n    "examples": ["He is resilient."],\n    "tags": ["GRE"]\n  }\n]`,
    txt: `resilient - adj. 有弹性的; 能迅速恢复的\nephemeral - adj. 短暂的; 朝生夕死的\nperseverance - n. 坚持不懈`,
  };

  const copyTemplate = (type: string) => {
    navigator.clipboard.writeText(sampleTemplates[type]);
    setCopiedFormat(type);
    showToast(`已复制 ${type.toUpperCase()} 模板范例到剪贴板`, 'success');
    setTimeout(() => setCopiedFormat(null), 2500);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">导入单词与词库</h2>
        <p className="text-sm text-slate-500 mt-1">
          支持从 CSV、TSV、JSON 或纯文本快速导入你的生词本与自定义词库，单次支持最高 50,000 行。
        </p>
      </div>

      {/* Success Commitment Banner */}
      {commitResult && (
        <div className="mb-8 p-6 bg-emerald-50 border border-emerald-200 rounded-2xl shadow-sm animate-in fade-in">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-emerald-950">导入成功入库！</h3>
              <p className="text-xs text-emerald-800 mt-1">
                已成功导入 <span className="font-bold">{commitResult.imported_count}</span> 条词条
                {commitResult.merged_count > 0 && `，合并补充 ${commitResult.merged_count} 条`}
                {commitResult.skipped_count > 0 && `，跳过重复 ${commitResult.skipped_count} 条`}
                {commitResult.cards_created !== undefined && `，自动生成 ${commitResult.cards_created} 张复习卡片`}。
              </p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <button
                  onClick={onNavigateToStudy}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors"
                >
                  立即开始复习学习 <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={onNavigateToWordbook}
                  className="px-4 py-2 bg-white hover:bg-emerald-100/60 text-emerald-900 border border-emerald-300 text-xs font-medium rounded-lg transition-colors"
                >
                  去词库查看词条
                </button>
                <button
                  onClick={() => {
                    setFile(null);
                    setPreviewData(null);
                    setCommitResult(null);
                  }}
                  className="px-4 py-2 text-xs text-slate-500 hover:text-slate-800"
                >
                  继续导入下一个文件
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload & Configuration Form */}
      {!commitResult && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handlePreviewUpload} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
              {/* File Dropzone */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  选择词库文件 * (最大 10MB)
                </label>
                <label className="border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer bg-slate-50/50 hover:bg-emerald-50/20 transition-all">
                  <UploadCloud className="w-10 h-10 text-slate-400 mb-2" />
                  <span className="text-sm font-semibold text-slate-700">
                    {file ? file.name : '点击选择文件，或将文件拖曳至此处'}
                  </span>
                  <span className="text-xs text-slate-400 mt-1">
                    {file ? `${(file.size / 1024).toFixed(1)} KB` : '支持 .csv, .tsv, .json, .txt'}
                  </span>
                  <input
                    type="file"
                    accept=".csv,.tsv,.json,.txt"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Format & Deck configuration */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    文件格式
                  </label>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value as ImportFormat)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                  >
                    <option value="auto">自动识别 (推荐)</option>
                    <option value="csv">CSV (逗号分隔)</option>
                    <option value="tsv">TSV (制表符分隔)</option>
                    <option value="json">JSON 数组格式</option>
                    <option value="txt">纯文本 TXT (每行一词)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    导入至目标词本
                  </label>
                  <select
                    value={selectedDeckId}
                    onChange={(e) => setSelectedDeckId(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                  >
                    <option value="">默认词本</option>
                    {decks.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!file || uploading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm disabled:opacity-50 transition-colors cursor-pointer"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${uploading ? 'animate-spin' : ''}`} />
                  {uploading ? '正在解析文件与校验列...' : '上传并预览解析结果'}
                </button>
              </div>
            </form>
          </div>

          {/* Quick Format Help & Sample Templates */}
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-xs text-slate-600 space-y-3">
              <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
                <Info className="w-4 h-4 text-emerald-600" />
                格式说明与规范
              </div>
              <p className="leading-relaxed">
                词条导入服务具备容错能力，会自动识别列名（如 term/word/front、meaning/definition/back 等）。
              </p>
              <div className="space-y-2 pt-2 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700">CSV 模板</span>
                  <button
                    onClick={() => copyTemplate('csv')}
                    className="flex items-center gap-1 text-[11px] text-emerald-700 hover:underline"
                  >
                    {copiedFormat === 'csv' ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                    复制范例
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700">JSON 格式</span>
                  <button
                    onClick={() => copyTemplate('json')}
                    className="flex items-center gap-1 text-[11px] text-emerald-700 hover:underline"
                  >
                    {copiedFormat === 'json' ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                    复制范例
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700">TXT 行分隔格式</span>
                  <button
                    onClick={() => copyTemplate('txt')}
                    className="flex items-center gap-1 text-[11px] text-emerald-700 hover:underline"
                  >
                    {copiedFormat === 'txt' ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                    复制范例
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Confirmation Panel */}
      {previewData && !commitResult && (
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-6 animate-in fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
            <div>
              <h3 className="text-lg font-bold text-slate-900">导入数据预览与确认</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                共解析 {previewData.rows} 行 • 有效 {previewData.valid_count} 条 • 发现重复 {previewData.duplicates} 条 • 错误 {previewData.error_count} 行
              </p>
            </div>

            {/* Duplicate Policy Selection */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-700">重复处理策略:</span>
              <div className="flex rounded-lg bg-slate-100 p-0.5">
                <button
                  type="button"
                  onClick={() => setDuplicatePolicy('skip')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    duplicatePolicy === 'skip'
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  跳过 (保留原词)
                </button>
                <button
                  type="button"
                  onClick={() => setDuplicatePolicy('merge')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    duplicatePolicy === 'merge'
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  合并 (补充例句释义)
                </button>
              </div>
            </div>
          </div>

          {/* Error rows notice if any */}
          {previewData.errors && previewData.errors.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                发现 {previewData.errors.length} 行数据存在格式问题（将被自动跳过）:
              </div>
              <ul className="list-disc list-inside space-y-0.5 text-amber-800 max-h-32 overflow-y-auto">
                {previewData.errors.slice(0, 10).map((err, i) => (
                  <li key={i}>
                    第 {err.row} 行: {err.message} {err.raw ? `[${err.raw.slice(0, 40)}]` : ''}
                  </li>
                ))}
                {previewData.errors.length > 10 && (
                  <li>... 其余 {previewData.errors.length - 10} 行错误已保留在服务端日志中</li>
                )}
              </ul>
            </div>
          )}

          {/* Sample Preview Table */}
          <div>
            <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
              有效词条前瞻 ({previewData.preview.length} 条示例)
            </h4>
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 font-semibold text-slate-600">
                    <th className="py-2.5 px-3">单词 (Term)</th>
                    <th className="py-2.5 px-3">音标 (Phonetic)</th>
                    <th className="py-2.5 px-3">中文释义 (Meaning)</th>
                    <th className="py-2.5 px-3">例句与标签</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {previewData.preview.slice(0, 10).map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50">
                      <td className="py-2.5 px-3 font-bold text-slate-900">{item.term}</td>
                      <td className="py-2.5 px-3 font-mono text-slate-500">
                        {item.phonetic ? `/${item.phonetic}/` : '-'}
                      </td>
                      <td className="py-2.5 px-3 text-slate-800">{item.meaning}</td>
                      <td className="py-2.5 px-3 text-slate-500">
                        {item.examples && item.examples.length > 0 && (
                          <div className="italic">"{formatExampleText(item.examples[0])}"</div>
                        )}
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex gap-1 mt-0.5">
                            {item.tags.map((t, i) => (
                              <span key={i} className="px-1 bg-slate-100 text-slate-600 rounded text-[10px]">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Commit Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setPreviewData(null)}
              className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              取消并重新选择
            </button>
            <button
              type="button"
              disabled={committing || previewData.valid_count === 0}
              onClick={handleCommit}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm disabled:opacity-50 transition-all cursor-pointer"
            >
              {committing ? '正在写入数据库...' : `确认入库 ${previewData.valid_count} 个生词`}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
